import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import type {
  Filter,
  FilterMatchMode,
} from "@/shared/components/filters/FilterSchema";
import { RoboqlQuery } from "@/shared/domain/query";
import { type Updater } from "@/shared/foundation/primitives/Table";

import type {
  ActiveFilters as ActiveFiltersState,
  FilterMenuOutput,
  RoboqlMenuConfig,
} from "../FilterModel";

import { RoboqlActiveFilters } from "./RoboqlActiveFilters";
import { StructuredActiveFilters } from "./StructuredActiveFilters";

export interface ActiveFiltersProps {
  /** Committed filter state. */
  filters: ActiveFiltersState;
  /** Filterable fields available in the inline `+` add-filter menu
   *  (structured variant only). Used to resolve each `filter.field`
   *  to its human label for chip rendering, and to populate the field
   *  picker. */
  filterDefinitions: FilterDefinition[];
  /** Called with the index of the filter the user clicked `×` on. */
  onRemove: (index: number) => void;
  /** Called with the filter's index and the updated filter when the user
   *  edits a filter. Wire to `view.updateFilter`. */
  onUpdate: (index: number, filter: Filter) => void;
  /** Called when the user clicks the right-aligned Clear button.
   *  Drops all filter state. */
  onClear: () => void;
  /** Called when the user picks a different option from the Match
   *  picker menu (visible only in `"structured"` mode with 2+ chips). */
  onMatchModeChange: (updater: Updater<FilterMatchMode>) => void;
  /** Called when the user commits a filter — from the inline `+`
   *  popover (structured) or the chip-edit popover (roboql). */
  onFilterSelected: (output: FilterMenuOutput) => void;
  /** When true, the structured `+` popover surfaces a Metrics tab
   *  alongside Properties. */
  metrics?: boolean;
  /** RoboQL configuration. Required when state can be `"roboql"`; the
   *  roboql variant component uses it to mount `<RoboqlFilterMenu>`
   *  inside the chip-edit popover. Optional for callers that never
   *  enter roboql mode (today's structured-only consumers). */
  roboql?: RoboqlMenuConfig;
  /** Controls rendered in the trailing group, ahead of Clear. The Save-as-View
   *  control mounts here, beside the filters it acts on. */
  trailingActions?: React.ReactNode;
  /** Controls a caller adds to the chip of a structured filter, given the
   *  filter and its index in the list. A RoboQL query has no chips per
   *  filter and draws none. */
  chipActions?: (filter: Filter, index: number) => React.ReactNode;
}

/**
 * Thin switch on `filters.type` that delegates to one of two per-variant
 * chip-row components. The structured `+` opens the shared `<FilterMenu>`
 * shell limited to structured modes (Properties + optional Metrics, never
 * RoboQL); the roboql variant mounts its own chip-edit editor. RoboQL is
 * never offered from a chip-bar gesture — it's a wholesale replacement of
 * all filters, so it stays on the main Filter button.
 */
export function ActiveFilters(props: ActiveFiltersProps) {
  const {
    filters,
    filterDefinitions,
    metrics,
    onClear,
    onFilterSelected,
    onMatchModeChange,
    onRemove,
    onUpdate,
    roboql,
    trailingActions,
    chipActions,
  } = props;

  switch (filters.type) {
    case "none":
      return null;
    case "structured":
      return (
        <StructuredActiveFilters
          filters={filters.filters}
          matchMode={filters.matchMode}
          filterDefinitions={filterDefinitions}
          metrics={metrics}
          onRemove={onRemove}
          onUpdate={onUpdate}
          onClear={onClear}
          onMatchModeChange={onMatchModeChange}
          onFilterSelected={(filter) =>
            onFilterSelected({ type: "structured", filter })
          }
          trailingActions={trailingActions}
          chipActions={chipActions}
        />
      );
    case "roboql":
      // `filters.type === "roboql"` implies a RoboQL config must have been
      // threaded through. A missing one is a programmer error.
      if (!roboql) {
        throw new Error(
          "RoboqlActiveFilters rendered without a `roboql` config. A consumer " +
            "that can enter RoboQL mode must pass `roboql` to " +
            "<ResourceView.ActiveFilters>.",
        );
      }
      return (
        <RoboqlActiveFilters
          query={filters.query}
          onRemove={onRemove}
          onClear={onClear}
          onExecute={(query, spec) =>
            onFilterSelected({
              type: "roboql",
              query: RoboqlQuery.from(query),
              spec,
            })
          }
          roboql={roboql}
          trailingActions={trailingActions}
        />
      );
  }
}
